import {Header, Pagination} from '@/components';
import {TableSkeleton} from '@/components/Table/TableSkeleton';

import BankFilters from './BankFilters';
import BankTable from './BankTable';
import {ANY_PROCESSOR, NOT_MAPPED, PAGE_SIZE} from './types';
import useBankList from './useBankList';

const resolveEmptyStateMessage = (hasCountries: boolean, hasError: boolean) => {
  if (!hasCountries) {
    return 'Pick at least one country to see its banks.';
  }
  if (hasError) {
    return "Couldn't load banks. Try again.";
  }
  return 'No banks match these filters.';
};

/**
 * Processors -> Banks. A read-only view of the bank directory by country, with
 * each bank's code for every processor that can pay into it. Filter to one
 * processor to see the exact code sent to it, or to "Not mapped" to find
 * banks no processor covers.
 */
const ProcessorBanks = () => {
  const {
    filters,
    setCountryCodes,
    setProcessorFilter,
    setSearch,
    list,
    total,
    filterKey,
    isLoading,
    error,
    onPageChange,
  } = useBankList();

  const selectedProcessor =
    filters.processorFilter === ANY_PROCESSOR ||
    filters.processorFilter === NOT_MAPPED
      ? undefined
      : filters.processorFilter;

  const loadedContent = list.length ? (
    <BankTable items={list} processor={selectedProcessor} />
  ) : (
    <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
      {resolveEmptyStateMessage(
        filters.countryCodes.length > 0,
        Boolean(error),
      )}
    </p>
  );

  return (
    <main className="my-5 mx-4 md:mx-10">
      <Header title="Banks" />
      <BankFilters
        filters={filters}
        onCountryCodesChange={setCountryCodes}
        onProcessorFilterChange={setProcessorFilter}
        onSearchChange={setSearch}
      />

      {filters.countryCodes.length && !isLoading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {total.toLocaleString()} {total === 1 ? 'bank' : 'banks'}
        </p>
      ) : null}

      <section className="pt-3">
        {isLoading ? <TableSkeleton /> : loadedContent}
      </section>

      {total > PAGE_SIZE ? (
        <Pagination
          key={filterKey}
          limit={PAGE_SIZE}
          onPageChange={onPageChange}
          total={total}
        />
      ) : null}
    </main>
  );
};

export default ProcessorBanks;
